import {
    type ShowroomNavViewItem,
    type ShowroomSectionItem,
    SectionSlot,
    isShowroomSectionItem,
    useFeatureConfig
} from '@makemydeal/dr-sales-checkin-components';
import { useIsMissingVehicle } from '@makemydeal/dr-sales-checkin-store';
import { FEATURES } from '@makemydeal/dr-sales-checkin-types';

import { useScrollEngine } from '../../hooks/useScrollEngine';
import { SECTION_COMPONENTS } from './sectionRegistry';
import { SectionStack } from './showroomSectionStack.style';

/**
 * A section whose own card can't execute (per its nav config) never gets a stack slot, so the
 * scroll engine's "first section" fallback lands on the next executable one instead. Vehicle
 * Details is the one exception: with no vehicle on the deal its card stays clickable (it
 * redirects to Browse Inventory — see `useNavigationHandler`) rather than disabled, so it is
 * excluded here by name instead, the same way a disabled card would be.
 */
const isStackableSection = (item: ShowroomNavViewItem, isMissingVehicle: boolean): item is ShowroomSectionItem =>
    isShowroomSectionItem(item) && !(item.key === FEATURES.VEHICLE_DETAILS && isMissingVehicle);

/**
 * TODO: HL:
 * - Rename ShowroomSectionStack to BuildYourOffer
 * - We need a useBuildYourOffer hook
 *
 */
const ShowroomSectionStack = () => {
    const { sections } = useFeatureConfig(SECTION_COMPONENTS);
    const isMissingVehicle = useIsMissingVehicle();

    const visibleItems = sections
        .flatMap(({ items }) => items)
        .filter((item): item is ShowroomSectionItem => isStackableSection(item, isMissingVehicle));
    const visibleSectionKeys = visibleItems.map(({ key }) => key);

    const { stackRef, getSectionRef, isContentMounted } = useScrollEngine({ sectionKeys: visibleSectionKeys });

    return (
        <SectionStack ref={stackRef} data-testid="showroom-section-stack">
            {visibleItems.map(({ key, label, component }) => (
                <SectionSlot
                    key={key}
                    sectionKey={key}
                    label={label}
                    sectionRef={getSectionRef(key)}
                    component={component}
                    isContentMounted={isContentMounted(key)}
                />
            ))}
        </SectionStack>
    );
};

export default ShowroomSectionStack;
